import { gql, useMutation } from '@apollo/client';
import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import { useFetchEQLFields } from '@modules/common/hooks';
import { useFetchMetadataFieldRelations } from '@modules/integrationsV2/hooks';
import { IntegrationsActionType } from '@modules/integrationsV2/store/integrationsActionType';
import { useIntegrationsStore } from '@modules/integrationsV2/store/integrationsStore';
import { IntegrationSource } from '@modules/integrationsV2/types';
import {
  getExistingFieldsRelation,
  getMetadataFieldRelationsByIntegrationType,
} from '@modules/integrationsV2/utils';
import {
  FieldRelation,
  Query,
  UpdateFieldRelationsInput,
} from 'generated/graphql';
import { useCallback } from 'react';
import { useGetUser } from 'v2/core/services/user';

const UPDATE_METADATA_FIELD_RELATIONS = gql`
  mutation updateFieldRelations(
    $tenantID: ID!
    $add: [FieldRelationInput!]
    $remove: [FieldRelationInput!]
  ) {
    updateFieldRelations(
      request: { tenantID: $tenantID, add: $add, remove: $remove }
    ) {
      updated
    }
  }
`;

interface AddOrReplaceFieldRelationsInput {
  fieldRelations: FieldRelation[];
  newFields: { field1: string; field2: string };
  existingFields: {
    field1?: string;
    field2?: string;
  };
}

interface RemoveFieldRelationsInput {
  fieldRelations: FieldRelation[];
  fieldsToRemove: { field1?: string; field2?: string };
}

export interface UpdateFieldRelationInput {
  field: string;
  integrationSource: IntegrationSource;
}

export const useUpdateMetadataFieldRelations = () => {
  const {
    tenant: { id: tenantID },
  } = useGetUser();

  const { showErrorNotification } = useNotificationContext();

  const dispatch = useIntegrationsStore(({ dispatch }) => dispatch);

  const [updateFieldRelationsMutation, { loading: isUpdatingFieldRelations }] =
    useMutation<Query, UpdateFieldRelationsInput>(
      UPDATE_METADATA_FIELD_RELATIONS,
      {
        notifyOnNetworkStatusChange: true,
        onError: () => {
          showErrorNotification({
            contentComponent: 'Failed to update field relations',
            props: {},
          });
        },
      },
    );

  const [getFieldRelations, { isLoading: isLoadingFieldRelations }] =
    useFetchMetadataFieldRelations();

  const { fields: allEqlFields } = useFetchEQLFields({
    fetchPolicy: 'cache-first', // We already fetch with cache-and-network at root level
  });

  const addOrReplaceFieldRelations = useCallback(
    async ({
      fieldRelations,
      newFields,
      existingFields,
    }: AddOrReplaceFieldRelationsInput) => {
      await updateFieldRelationsMutation({
        variables: {
          tenantID,
          add: [
            {
              field1: newFields.field1,
              field2: newFields.field2,
            },
          ],
          remove:
            existingFields.field1 && existingFields.field2
              ? [
                  {
                    field1: existingFields.field1,
                    field2: existingFields.field2,
                  },
                ]
              : undefined,
        },
      });

      let updatedFieldRelations: FieldRelation[] = [];

      if (existingFields.field1 && existingFields.field2) {
        updatedFieldRelations = fieldRelations.map((fieldRelation) => {
          const possibleRelation1 =
            fieldRelation.field1 === existingFields.field1 &&
            fieldRelation.field2 === existingFields.field2;

          const possibleRelation2 =
            fieldRelation.field1 === existingFields.field2 &&
            fieldRelation.field2 === existingFields.field1;

          if (possibleRelation1 || possibleRelation2) {
            return {
              field1: newFields.field1,
              field2: newFields.field2,
            };
          }
          return fieldRelation;
        });
      } else {
        updatedFieldRelations = [
          ...fieldRelations,
          {
            field1: newFields.field1,
            field2: newFields.field2,
          },
        ];
      }

      dispatch({
        type: IntegrationsActionType.SET_DATA,
        payload: {
          fieldRelationsByType: getMetadataFieldRelationsByIntegrationType({
            fieldRelations: updatedFieldRelations,
            allEqlFields,
          }),
        },
      });
    },
    [allEqlFields, dispatch, tenantID, updateFieldRelationsMutation],
  );

  const removeFieldRelations = useCallback(
    async ({ fieldRelations, fieldsToRemove }: RemoveFieldRelationsInput) => {
      if (!fieldsToRemove.field1 || !fieldsToRemove.field2) {
        return;
      }

      await updateFieldRelationsMutation({
        variables: {
          tenantID,
          remove: [
            {
              field1: fieldsToRemove.field1,
              field2: fieldsToRemove.field2,
            },
          ],
        },
      });

      const updatedFieldRelations = fieldRelations.filter((fieldRelation) => {
        const possibleRelation1 =
          fieldRelation.field1 === fieldsToRemove.field1 &&
          fieldRelation.field2 === fieldsToRemove.field2;

        const possibleRelation2 =
          fieldRelation.field1 === fieldsToRemove.field2 &&
          fieldRelation.field2 === fieldsToRemove.field1;

        return !(possibleRelation1 || possibleRelation2);
      });

      dispatch({
        type: IntegrationsActionType.SET_DATA,
        payload: {
          fieldRelationsByType: getMetadataFieldRelationsByIntegrationType({
            fieldRelations: updatedFieldRelations,
            allEqlFields,
          }),
        },
      });
    },
    [allEqlFields, dispatch, tenantID, updateFieldRelationsMutation],
  );

  const updateFieldRelations = useCallback(
    async ({
      sourceFieldInput,
      targetFieldInput,
      isRemoveAction,
    }: {
      sourceFieldInput: UpdateFieldRelationInput;
      targetFieldInput: UpdateFieldRelationInput;
      isRemoveAction?: boolean;
    }) => {
      try {
        const fieldRelationsData = await getFieldRelations();
        const fieldRelations =
          fieldRelationsData?.data?.getFieldRelations?.relations ?? [];

        const fieldRelationsByType = getMetadataFieldRelationsByIntegrationType(
          {
            fieldRelations,
            allEqlFields,
          },
        );

        const {
          relatedSourceField: existingSourceField,
          relatedTargetField: existingTargetField,
        } = getExistingFieldsRelation({
          fieldRelationsByType,
          sourceIntegrationSource: sourceFieldInput.integrationSource,
          targetIntegrationSource: targetFieldInput.integrationSource,
        });

        if (isRemoveAction) {
          await removeFieldRelations({
            fieldRelations,
            fieldsToRemove: {
              field1: existingSourceField,
              field2: existingTargetField,
            },
          });
          return;
        }

        await addOrReplaceFieldRelations({
          fieldRelations,
          newFields: {
            field1: sourceFieldInput.field,
            field2: targetFieldInput.field,
          },
          existingFields: {
            field1: existingSourceField,
            field2: existingTargetField,
          },
        });
      } catch {
        // do nothing, error is handled by mutation hook's onError
      }
    },
    [
      addOrReplaceFieldRelations,
      allEqlFields,
      getFieldRelations,
      removeFieldRelations,
    ],
  );

  const isLoading = isUpdatingFieldRelations || isLoadingFieldRelations;

  return [updateFieldRelations, { isLoading }] as const;
};
